<template>
  <tr-dialog
    ref="modal"
    :unique-name="true"
    name="payin-refund-dialog"
    title="Devolver recebimento"
    :has-its-own-buttons="true"
    :on-close="close"
  >
    <bg-stack
      v-if="currentStep === steps.FILL"
      space="md"
    >
      <bg-text
        type="body-md"
        color="grey-dark"
      >
        {{ props.message }}
      </bg-text>
      <div>
        <tr-text-field
          ref="input"
          v-model="refundModelAmount"
          label="Quanto deseja devolver?"
          :disabled="inputDisabled"
          :dont-use-col-classes="true"
          :no-margin="true"
          name="refundAmount"
          type="currency"
          placeholder="R$ 0,00"
          :required="true"
          error-message="Digite um valor para devolução"
          @vue:mounted="onInputMounted"
        />
        <bg-text
          v-if="isAboveRefundLimit"
          type="label"
          color="red-semi-dark"
          role="alert"
        >
          O valor máximo por devolução é {{ centsToReal(MAX_REFUND_AMOUNT_CENTS) }}
        </bg-text>
        <bg-text
          v-else
          type="label"
          color="grey-semi-dark"
        >
          Você pode devolver até {{ centsToReal(maxRefundAmountCents) }}
        </bg-text>
      </div>
    </bg-stack>
    <template v-else>
      <bg-text
        type="body-md"
        color="grey-dark"
      >
        Tem certeza que deseja devolver {{ $format.currency(refundModelAmount) }} para {{ props.payerName }}?
      </bg-text>
    </template>
    <tr-dialog-buttons-container>
      <bg-button
        class="pull-left"
        variant="outline"
        @click="stepCtrls[currentStep].cancel()"
      >
        Cancelar
      </bg-button>
      <bg-button
        size="md"
        class="pull-right"
        :disabled="isSubmitDisabled"
        @click="stepCtrls[currentStep].submit()"
      >
        {{ stepCtrls[currentStep].submitLabel }}
      </bg-button>
    </tr-dialog-buttons-container>
  </tr-dialog>
</template>

<script setup>
import checkpoint from '@/commons/constants/checkpoint';
import checkpointResource from '@/commons/resources/checkpoint';
import { useErrorHandler } from '@/commons/composables/useErrorHandler';
import { useToaster } from '@/commons/composables/useToaster';
import { BgText, BgButton, BgStack } from '@transfeeradev/bridge';
import { computed, reactive, ref } from 'vue';
import { refund } from '@/apps/payin/payins/services/payins';
import { centsToReal, realToCents } from '@/commons/formatters/currency';

// Maximum amount allowed per refund: R$ 15,000.00
const MAX_REFUND_AMOUNT_CENTS = 1500000;

const steps = Object.freeze({
  FILL: 'fill',
  CONFIRM: 'confirm'
});

defineProps({
  onRefund: {
    type: Function
  }
});

const emit = defineEmits(['save']);
const toaster = useToaster();
const { handleError } = useErrorHandler();

const input = ref(null);
const inputDisabled = ref(false);
const modal = ref();
const currentStep = ref(steps.FILL);
const refundModelAmount = ref(0);

const props = reactive({
  fullOnly: false,
  message: '',
  refundableAmountCents: 0,
  payerName: '',
  payinId: ''
});

// Full-only refunds cannot be split, so the per-refund limit
// only applies to payins that accept partial refunds.
const maxRefundAmountCents = computed(() =>
  props.fullOnly ? props.refundableAmountCents : Math.min(props.refundableAmountCents, MAX_REFUND_AMOUNT_CENTS)
);

const isAboveRefundLimit = computed(
  () => !props.fullOnly && realToCents(refundModelAmount.value) > MAX_REFUND_AMOUNT_CENTS
);

const isSubmitDisabled = computed(
  () => refundModelAmount.value <= 0 || realToCents(refundModelAmount.value) > maxRefundAmountCents.value
);

const stepCtrls = Object.freeze({
  [steps.FILL]: {
    cancel: () => close(),
    submit: () => (currentStep.value = steps.CONFIRM),
    submitLabel: 'Devolver'
  },
  [steps.CONFIRM]: {
    cancel: () => toFill(),
    submit: () => save(props.payinId, refundModelAmount.value),
    submitLabel: 'Confirmar'
  }
});

const FULL_ONLY_DEFAULT_MESSAGE = 'Este recebimento não pode ser devolvido parcialmente';
const PARTIAL_DEFAULT_MESSAGE = `Este recebimento pode ser devolvido parcialmente. Cada devolução pode ser de até ${centsToReal(
  MAX_REFUND_AMOUNT_CENTS
)}. Caso o valor do recebimento seja superior, divida em mais devoluções até completar o valor total.`;

const open = ({ fullOnly = false, message, refundableAmountCents, payerName, payinId }) => {
  props.payinId = payinId;
  props.message = message || (fullOnly ? FULL_ONLY_DEFAULT_MESSAGE : PARTIAL_DEFAULT_MESSAGE);
  props.fullOnly = fullOnly;
  props.payerName = payerName;
  props.refundableAmountCents = refundableAmountCents;

  modal.value.open();
};

const onInputMounted = () => {
  inputDisabled.value = false;

  if (props.fullOnly) {
    input.value.updateValue(props.refundableAmountCents / 100);
    inputDisabled.value = true;
  }
};

const toFill = () => {
  currentStep.value = steps.FILL;
};

const close = () => {
  modal.value.close();
  refundModelAmount.value = 0;
};

const save = async (id, amount) => {
  try {
    await refund(id, amount);
    close();
    emit('save');
    toaster.success('Valor devolvido com sucesso');
    checkpointResource.create(checkpoint.PAYIN_REFUNDED);
  } catch (e) {
    handleError(e, 'Ocorreu um erro na devolução de valor');
  }
};

defineExpose({
  open
});
</script>
